/**
 * Registering someone who is not Brazilian.
 *
 * Every identity rule on the cadastro assumes a CPF: it is the required field, it is what the
 * duplicate check runs on, and the phone input masks to "(DD) XXXXX-XXXX". None of that applies
 * to a foreigner, so the "é estrangeiro" declaration switches the rules over — passport instead
 * of CPF, an international phone, and the country that issued the passport.
 *
 * These are the pure rules; PersonalInfoSection renders them and condo-manager enforces them
 * again on the way in (handlers/person_foreigner.go, which must agree with this file).
 */

/**
 * ISO 3166-1 alpha-2, minus the territories with no nationality to hold (Antarctica and the
 * uninhabited islands) — noise in a list the operator has to scroll.
 */
const COUNTRY_CODES =
  'AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS ' +
  'BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE ' +
  'EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN ' +
  'HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC ' +
  'LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ ' +
  'NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS ' +
  'RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM ' +
  'TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW';

/**
 * Country names come from the platform rather than a table of our own: the list is correct,
 * localized and maintained by ICU, and a hand-written one drifts the moment a country is
 * renamed. Built once — constructing a DisplayNames per row costs real time on a 200-row list.
 */
const regionNames: { of(code: string): string | undefined } | null = (() => {
  try {
    return new (Intl as any).DisplayNames(['pt-BR'], { type: 'region', fallback: 'code' });
  } catch {
    return null;
  }
})();

/**
 * What ICU answers for a code it cannot place ("Região desconhecida").
 *
 * ZZ is a real CLDR region meaning "unknown", so `fallback: 'code'` does not catch it — the
 * lookup succeeds and returns that phrase, which would be printed in the country column of a
 * record whose code is simply one this build does not know. Comparing against the sentinel is
 * what turns that back into the code.
 */
const UNKNOWN_REGION = (() => {
  try {
    return regionNames?.of('ZZ');
  } catch {
    return undefined;
  }
})();

/** The country's name in Portuguese, or the code itself when this build cannot name it. */
export function countryName(code?: string | null): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  try {
    const name = regionNames?.of(upper);
    if (!name || name === upper || name === UNKNOWN_REGION) return upper;
    return name;
  } catch {
    return upper;
  }
}

let cachedOptions: Array<{ code: string; name: string }> | null = null;

/** Every country, named and sorted the way the dropdown shows them. */
export function countryOptions(): Array<{ code: string; name: string }> {
  if (!cachedOptions) {
    cachedOptions = COUNTRY_CODES.split(' ')
      .map((code) => ({ code, name: countryName(code) }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }
  return cachedOptions;
}

/**
 * The passport number as it should be stored: upper-case, separators removed.
 *
 * Mirrors the backend's storedPassport. The number is transcribed off a physical document, so
 * casing and spacing are the transcriber's, not the passport's — keeping them means the same
 * document fails to match itself on the next visit.
 */
export function normalizePassport(value?: string | null): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * A foreign phone number, kept legible instead of masked.
 *
 * The Brazilian mask slices the first two digits into a DDD, turning "+58 412 1234567" into
 * "(58) 41212-34567". The leading "+" is forced because that is also what tells the backend to
 * leave the number alone: utils.NormalizePhone passes through anything carrying a country code
 * other than +55 and masks everything else as Brazilian.
 */
export function formatForeignPhone(value: string): string {
  const cleaned = (value || '').replace(/[^\d\s()+-]/g, '').replace(/\+/g, '');
  const trimmed = cleaned.replace(/^\s+/, '');
  if (!trimmed || !/\d/.test(trimmed)) return '';
  return `+${trimmed}`.slice(0, 24);
}

/** The number to open WhatsApp on: digits only, with the right country code already in place. */
export function whatsappNumber(phone: string, isForeigner: boolean): string {
  const digits = (phone || '').replace(/\D/g, '');
  if (isForeigner) return digits; // the typed country code IS the prefix
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
}

/** Whether the CPF field has to be filled in before the form can be submitted. */
export function isCpfRequired(opts: {
  /** The customer's field configuration (or the caller's default). */
  requiredByConfig: boolean;
  /** Whether this operator may waive it — see canRegisterWithoutCPF. */
  waiverAllowed: boolean;
  waiverChecked: boolean;
  /** A foreigner has no CPF, so it is never required of one. */
  isForeigner: boolean;
}): boolean {
  if (!opts.requiredByConfig) return false;
  if (opts.isForeigner) return false;
  return !(opts.waiverAllowed && opts.waiverChecked);
}

/** The three fields as they live in a registration form. */
export interface ForeignerFormFields {
  is_foreigner?: boolean;
  passport_number?: string;
  country?: string;
}

/**
 * The foreigner fields as the API takes them — spread into EVERY person create/update payload.
 *
 * Every cadastro renders one shared section but its own page hand-builds the request body from
 * an explicit field list, so a new field reaches the backend only where somebody remembered to
 * add it. A helper plus the ratchet in personPayloadForeignerFields.test.ts is what keeps the
 * nine of them in step.
 *
 * Omitted rather than nulled when the person is not a foreigner: an absent key means "no change"
 * to the backend, so a form that does not collect a passport cannot erase one.
 */
export function foreignerPayload(data: ForeignerFormFields): {
  is_foreigner: boolean;
  passport_number?: string;
  country?: string;
} {
  const isForeigner = !!data.is_foreigner;
  if (!isForeigner) return { is_foreigner: false };
  return {
    is_foreigner: true,
    passport_number: normalizePassport(data.passport_number) || undefined,
    country: data.country || undefined,
  };
}

/** The validation message for the passport field, or undefined when it is acceptable. */
export function passportError(isForeigner: boolean, passport?: string | null): string | undefined {
  if (!isForeigner) return undefined;
  if (!normalizePassport(passport)) return 'Número do passaporte é obrigatório';
  return undefined;
}

/**
 * Whether the person is identified well enough to submit — the rule the SUBMIT BUTTON reads.
 *
 * It exists because the visitor cadastro gated its button on a second, hand-written copy of the
 * CPF rule that knew nothing about the declaration: the form hid the CPF for a foreigner, the
 * operator filled in everything that remained, and "Liberar Acesso" stayed grey because of a
 * field they could no longer see. The field and the button have to read the same rule.
 *
 * `cpfRequired` is the answer from isCpfRequired — it already folds in the field config, the
 * admin waiver and the declaration.
 */
export function identityComplete(opts: {
  cpfFieldEnabled: boolean;
  cpfRequired: boolean;
  cpf?: string;
  isForeigner: boolean;
  passport?: string;
  /** Somebody already on file without a CPF was approved without one; do not block them now. */
  existingPersonWithoutCpf: boolean;
}): boolean {
  // The passport is the document that REPLACES the CPF, so it is owed even where the CPF was
  // waived — without it the record identifies nobody.
  if (opts.isForeigner) return !!normalizePassport(opts.passport);
  if (!opts.cpfFieldEnabled || !opts.cpfRequired) return true;
  if (opts.existingPersonWithoutCpf) return true;
  return !!(opts.cpf && opts.cpf.trim());
}
